import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import PrivateRoute from './components/common/PrivateRoute';
import AdminRoute from './components/common/AdminRoute';
import KYCProtectedRoute from './components/common/KYCProtectedRoute';
import KYCRedirect from './components/common/KYCRedirect';

// Layout Components
import Layout from './components/layout/Layout';
import AdminLayout from './components/layout/AdminLayout';

// Auth Pages
import Login from './pages/auth/Login';
import SignUp from './pages/auth/SignUp';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import ReferralRedirect from './components/auth/ReferralRedirect';

// Client Pages
import Dashboard from './pages/client/Dashboard';
import Profile from './pages/client/Profile';
import OrderHistory from './pages/client/OrderHistory';
import Kyc from './pages/client/Kyc';
import Wallet from './pages/client/Wallet';
import Deposit from './pages/client/Deposit';
import Withdrawal from './pages/client/Withdrawal';
import Transfer from './pages/client/Transfer';
import Support from './pages/client/Support';
import DemoAccounts from './pages/accounts/DemoAccounts';
import LiveAccounts from './pages/accounts/LiveAccounts';
import LeverageHistory from './pages/accounts/LeverageHistory';
import MyTrades from './pages/accounts/MyTrades';
import Analytics from './pages/analytics/Analytics';
import FAQ from './pages/faq/FAQ';
import QuickLinks from './pages/quick-links/QuickLinks';
import IBAccounts from './pages/ib-accounts/IBAccounts';
import WebTerminal from './pages/client/WebTerminal';
import AccountSettings from './pages/client/AccountSettings';
import UserEconomicCalendar from './pages/client/EconomicCalendar';

// IB Pages
import IBDashboard from './pages/ib/Dashboard';
import IBList from './pages/ib/IBList';
import IBClients from './pages/ib/Clients';
import IBCommissions from './pages/ib/Commissions';
import IBWallet from './pages/ib/Wallet';
import IBRebates from './pages/ib/Rebates';
import IBReports from './pages/ib/Reports';
import IBMarketingTools from './pages/ib/MarketingTools';
import IBKyc from './pages/ib/Kyc';
import IBSettings from './pages/ib/Settings';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import Clients from './pages/admin/Clients';
import Trades from './pages/admin/Trades';
import Reports from './pages/admin/Reports';
import Payments from './pages/admin/Payments';
import Leads from './pages/admin/Leads';
import AdminSupport from './pages/admin/AdminSupport';
import AdminAccounts from './pages/admin/AdminAccounts';
import IBMarketing from './pages/admin/IBMarketing';
import Settings from './pages/admin/Settings';
import AdminEconomicCalendar from './pages/admin/EconomicCalendar';

// PAMM Admin Pages
import PAMMStrategies from './pages/admin/pamm/Strategies';
import PAMMAllocations from './pages/admin/pamm/Allocations';
import PAMMCommissions from './pages/admin/pamm/Commissions';
import PAMMTrades from './pages/admin/pamm/Trades';
import PAMMApprovals from './pages/admin/pamm/Approvals';
import PAMMAccounts from './pages/admin/pamm/Accounts';

// MAM Admin Pages
import MAMAccounts from './pages/admin/mam/Accounts';
import MAMInvestors from './pages/admin/mam/Investors';
import MAMAllocations from './pages/admin/mam/Allocations';
import MAMCommissions from './pages/admin/mam/Commissions';
import MAMTrades from './pages/admin/mam/Trades';

// Copy Trading Admin Pages
import CopyTradingDashboard from './pages/admin/copy-trading/Dashboard';
import CopyTradingStrategies from './pages/admin/copy-trading/Strategies';
import CopyTradingManagers from './pages/admin/copy-trading/Managers';
import CopyTradingFollowers from './pages/admin/copy-trading/Followers';
import CopyTradingPerformance from './pages/admin/copy-trading/Performance';
import CopyTradingTrades from './pages/admin/copy-trading/Trades';
// import CopyTradingSettings from './pages/admin/copy-trading/Settings';
import CopyTradingReports from './pages/admin/copy-trading/Reports';

// Admin Trading Services Pages
import AdminStrategyApproval from './pages/admin/trading-services/StrategyApproval';
import AdminPAMMApproval from './pages/admin/trading-services/PAMMApproval';
import AdminRiskMonitoring from './pages/admin/trading-services/RiskMonitoring';
import AdminAuditLogs from './pages/admin/trading-services/AuditLogs';

// Copy Trading User Pages
import CopyTradingUserDashboard from './pages/copy-trading/Dashboard';
import CopyTradingMarketplace from './pages/copy-trading/Marketplace';
import CopyTradingStrategyDetails from './pages/copy-trading/StrategyDetails';
import CopyTradingMyCopies from './pages/copy-trading/MyCopies';
import CopyTradingLiveTrades from './pages/copy-trading/LiveTrades';
import CopyTradingUserTradingHistory from './pages/copy-trading/TradingHistory';
import CopyTradingRiskManagement from './pages/copy-trading/RiskManagement';
import CopyTradingProviderDashboard from './pages/copy-trading/ProviderDashboard';

// Trading Services User Pages
import TradingPerformance from './pages/trading-services/Performance';
import TradingTradeHistory from './pages/trading-services/TradeHistory';
import TradingFees from './pages/trading-services/Fees';
import TradingReports from './pages/trading-services/Reports';

// Error Pages
import NotFound from './pages/error/NotFound';
import Unauthorized from './pages/error/Unauthorized';
import {
  ADMIN_ROUTES,
  PUBLIC_ROUTES,
  USER_ROUTES,
  IB_ROUTES,
  PAMM_ROUTES,
  MAM_ROUTES,
} from './constants/routeConstants';

import PAMMFundManagement from './modules/pamm/pages/FundManagement';
import PAMMCreateFund from './modules/pamm/pages/CreateFund';
import PAMMEditFund from './modules/pamm/pages/EditFund';
import PAMMFundDetails from './modules/pamm/pages/FundDetails';
import PAMMInvestorDetails from './modules/pamm/pages/InvestorDetails';
import PAMMInvestmentRequests from './modules/pamm/pages/InvestmentRequests';
import PAMMDeposits from './modules/pamm/pages/Deposits';
import PAMMWithdrawals from './modules/pamm/pages/Withdrawals';
import PAMMProfitDistribution from './modules/pamm/pages/ProfitDistribution';
import PAMMAllocationHistory from './modules/pamm/pages/AllocationHistory';
import PAMMOpenTrades from './modules/pamm/pages/OpenTrades';
import PAMMClosedTrades from './modules/pamm/pages/ClosedTrades';
import PAMMFeesCommission from './modules/pamm/pages/FeesCommission';
import PAMMStatements from './modules/pamm/pages/Statements';
import PAMMDashboard from './modules/pamm/pages/Dashboard';
import PAMMInvestors from './modules/pamm/pages/Investors';
import PAMMPerformance from './modules/pamm/pages/Performance';
import PAMMReports from './modules/pamm/pages/Reports';
import MAMDashboard from './modules/mam/pages/Dashboard';
import MAMManagers from './modules/mam/pages/Managers';
import MAMCreateManager from './modules/mam/pages/CreateManager';
import MAMManagerDetails from './modules/mam/pages/ManagerDetails';
import MAMClientAccounts from './modules/mam/pages/ClientAccounts';
import MAMAllocation from './modules/mam/pages/Allocation';
import MAMModuleTrades from './modules/mam/pages/Trades';
import MAMPerformance from './modules/mam/pages/Performance';
import MAMReports from './modules/mam/pages/Reports';
import MAMAuditLogs from './modules/mam/pages/AuditLogs';

const App = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <div className="App">
            <Toaster
              position="bottom-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
              }}
            />

            <Routes>
              <Route path={PUBLIC_ROUTES.LOGIN} element={<Login />} />
              {/* <Route path={PUBLIC_ROUTES.REGISTER} element={<Register />} /> */}
              <Route path={PUBLIC_ROUTES.SIGNUP} element={<SignUp />} />
              <Route
                path={PUBLIC_ROUTES.FORGOT_PASSWORD}
                element={<ForgotPassword />}
              />
              <Route
                path={PUBLIC_ROUTES.RESET_PASSWORD}
                element={<ResetPassword />}
              />
              <Route path="/ref" element={<ReferralRedirect />} />{' '}
              <Route path="/" element={<Layout />}>
                <Route
                  index
                  element={<Navigate to={USER_ROUTES.USER_DASHBOARD} replace />}
                />
                <Route
                  path={USER_ROUTES.USER_DASHBOARD}
                  element={
                    <PrivateRoute>
                      <Dashboard />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_PROFILE}
                  element={
                    <PrivateRoute>
                      <Profile />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_SETTINGS}
                  element={
                    <PrivateRoute>
                      <AccountSettings />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_ORDER_HISTORY}
                  element={
                    <PrivateRoute>
                      <OrderHistory />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_WALLET}
                  element={
                    <PrivateRoute>
                      <Wallet />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_PAYMENTS}
                  element={
                    <PrivateRoute>
                      <KYCRedirect to={USER_ROUTES.USER_KYC} delay={2000}>
                        <Deposit />
                      </KYCRedirect>
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_DEPOSIT}
                  element={
                    <PrivateRoute>
                      <KYCProtectedRoute>
                        <Deposit />
                      </KYCProtectedRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_WITHDRAWAL}
                  element={
                    <PrivateRoute>
                      <KYCProtectedRoute>
                        <Withdrawal />
                      </KYCProtectedRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_TRANSFER}
                  element={
                    <PrivateRoute>
                      <Transfer />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_KYC}
                  element={
                    <PrivateRoute>
                      <Kyc />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_SUPPORTS}
                  element={
                    <PrivateRoute>
                      <Support />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_ACCOUNT_DEMO}
                  element={
                    <PrivateRoute>
                      <DemoAccounts />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_ACCOUNT_LIVE}
                  element={
                    <PrivateRoute>
                      <LiveAccounts />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_ACCOUNT_LEVERAGE_HISTORY}
                  element={
                    <PrivateRoute>
                      <LeverageHistory />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_ACCOUNT_MY_TRADES}
                  element={
                    <PrivateRoute>
                      <MyTrades />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_ANALYTICS}
                  element={
                    <PrivateRoute>
                      <Analytics />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_FAQ}
                  element={
                    <PrivateRoute>
                      <FAQ />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_QUICK_LINKS}
                  element={
                    <PrivateRoute>
                      <QuickLinks />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_IB_ACCOUNTS}
                  element={
                    <PrivateRoute>
                      <IBAccounts />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={IB_ROUTES.USER_IB_DASHBOARD}
                  element={
                    <PrivateRoute>
                      <IBDashboard />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={IB_ROUTES.USER_IB_LIST}
                  element={
                    <PrivateRoute>
                      <IBList />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={IB_ROUTES.USER_IB_CLIENTS}
                  element={
                    <PrivateRoute>
                      <IBClients />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={IB_ROUTES.USER_IB_COMMISSIONS}
                  element={
                    <PrivateRoute>
                      <IBCommissions />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={IB_ROUTES.USER_IB_WALLET}
                  element={
                    <PrivateRoute>
                      <IBWallet />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={IB_ROUTES.USER_IB_REBATES}
                  element={
                    <PrivateRoute>
                      <IBRebates />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={IB_ROUTES.USER_IB_REPORTS}
                  element={
                    <PrivateRoute>
                      <IBReports />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={IB_ROUTES.USER_IB_MARKETING_TOOLS}
                  element={
                    <PrivateRoute>
                      <IBMarketingTools />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={IB_ROUTES.USER_IB_KYC}
                  element={
                    <PrivateRoute>
                      <IBKyc />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={IB_ROUTES.USER_IB_SETTINGS}
                  element={
                    <PrivateRoute>
                      <IBSettings />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_WEB_TERMINAL}
                  element={
                    <PrivateRoute>
                      <WebTerminal />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_ECONOMIC_CALENDAR}
                  element={
                    <PrivateRoute>
                      <UserEconomicCalendar />
                    </PrivateRoute>
                  }
                />

                {/* ── PAMM Routes ── */}
                <Route
                  path={PAMM_ROUTES.PAMM_DASHBOARD}
                  element={
                    <PrivateRoute>
                      <PAMMDashboard />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={PAMM_ROUTES.PAMM_FUND_MANAGEMENT}
                  element={
                    <PrivateRoute>
                      <PAMMFundManagement />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={PAMM_ROUTES.PAMM_CREATE_FUND}
                  element={
                    <PrivateRoute>
                      <PAMMCreateFund />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={`${PAMM_ROUTES.PAMM_EDIT_FUND}/:id`}
                  element={
                    <PrivateRoute>
                      <PAMMEditFund />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={`${PAMM_ROUTES.PAMM_FUND_DETAILS}/:id`}
                  element={
                    <PrivateRoute>
                      <PAMMFundDetails />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={PAMM_ROUTES.PAMM_INVESTORS}
                  element={
                    <PrivateRoute>
                      <PAMMInvestors />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={`${PAMM_ROUTES.PAMM_INVESTOR_DETAILS}/:id`}
                  element={
                    <PrivateRoute>
                      <PAMMInvestorDetails />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={PAMM_ROUTES.PAMM_INVESTMENT_REQUESTS}
                  element={
                    <PrivateRoute>
                      <PAMMInvestmentRequests />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={PAMM_ROUTES.PAMM_DEPOSITS}
                  element={
                    <PrivateRoute>
                      <PAMMDeposits />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={PAMM_ROUTES.PAMM_WITHDRAWALS}
                  element={
                    <PrivateRoute>
                      <PAMMWithdrawals />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={PAMM_ROUTES.PAMM_PROFIT_DISTRIBUTION}
                  element={
                    <PrivateRoute>
                      <PAMMProfitDistribution />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={PAMM_ROUTES.PAMM_ALLOCATION_HISTORY}
                  element={
                    <PrivateRoute>
                      <PAMMAllocationHistory />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={PAMM_ROUTES.PAMM_OPEN_TRADES}
                  element={
                    <PrivateRoute>
                      <PAMMOpenTrades />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={PAMM_ROUTES.PAMM_CLOSED_TRADES}
                  element={
                    <PrivateRoute>
                      <PAMMClosedTrades />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={PAMM_ROUTES.PAMM_PERFORMANCE}
                  element={
                    <PrivateRoute>
                      <PAMMPerformance />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={PAMM_ROUTES.PAMM_FEES_COMMISSION}
                  element={
                    <PrivateRoute>
                      <PAMMFeesCommission />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={PAMM_ROUTES.PAMM_REPORTS}
                  element={
                    <PrivateRoute>
                      <PAMMReports />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={PAMM_ROUTES.PAMM_STATEMENTS}
                  element={
                    <PrivateRoute>
                      <PAMMStatements />
                    </PrivateRoute>
                  }
                />
                {/* <Route
                  path={PAMM_ROUTES.PAMM_NOTIFICATIONS}
                  element={
                    <PrivateRoute>
                      <PAMMNotifications />
                    </PrivateRoute>
                  }
                /> */}
                {/* <Route
                  path={PAMM_ROUTES.PAMM_AUDIT_LOGS}
                  element={
                    <PrivateRoute>
                      <PAMMAuditLogs />
                    </PrivateRoute>
                  } 
                />*}

                {/* ── MAM Routes ── */}
                <Route
                  path={MAM_ROUTES.MAM_DASHBOARD}
                  element={
                    <PrivateRoute>
                      <MAMDashboard />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={MAM_ROUTES.MAM_MANAGERS}
                  element={
                    <PrivateRoute>
                      <MAMManagers />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={MAM_ROUTES.MAM_CREATE_MANAGER}
                  element={
                    <PrivateRoute>
                      <MAMCreateManager />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={`${MAM_ROUTES.MAM_MANAGER_DETAILS}/:id`}
                  element={
                    <PrivateRoute>
                      <MAMManagerDetails />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={MAM_ROUTES.MAM_CLIENT_ACCOUNTS}
                  element={
                    <PrivateRoute>
                      <MAMClientAccounts />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={MAM_ROUTES.MAM_ALLOCATION}
                  element={
                    <PrivateRoute>
                      <MAMAllocation />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={MAM_ROUTES.MAM_TRADES}
                  element={
                    <PrivateRoute>
                      <MAMModuleTrades />
                    </PrivateRoute>
                  }
                />
                {/* <Route
                  path={MAM_ROUTES.MAM_NOTIFICATIONS}
                  element={
                    <PrivateRoute>
                      <MAMNotifications />
                    </PrivateRoute>
                  }
                /> */}
                <Route
                  path={MAM_ROUTES.MAM_AUDIT_LOGS}
                  element={
                    <PrivateRoute>
                      <MAMAuditLogs />
                    </PrivateRoute>
                  }
                />

                {/* ── Copy Trading Routes ── */}
                <Route
                  path={USER_ROUTES.USER_COPY_TRADING_DASHBOARD}
                  element={
                    <PrivateRoute>
                      <CopyTradingUserDashboard />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_COPY_TRADING_MARKETPLACE}
                  element={
                    <PrivateRoute>
                      <CopyTradingMarketplace />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_COPY_TRADING_STRATEGY_DETAILS}
                  element={
                    <PrivateRoute>
                      <CopyTradingStrategyDetails />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_COPY_TRADING_ACTIVE}
                  element={
                    <PrivateRoute>
                      <CopyTradingMyCopies />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_COPY_TRADING_LIVE_TRADES}
                  element={
                    <PrivateRoute>
                      <CopyTradingLiveTrades />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_COPY_TRADING_HISTORY}
                  element={
                    <PrivateRoute>
                      <CopyTradingUserTradingHistory />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={USER_ROUTES.USER_COPY_TRADING_RISK}
                  element={
                    <PrivateRoute>
                      <CopyTradingRiskManagement />
                    </PrivateRoute>
                  }
                />
                {/* <Route
                  path={USER_ROUTES.USER_COPY_TRADING_NOTIFICATIONS}
                  element={
                    <PrivateRoute>
                      <CopyTradingNotifications />
                    </PrivateRoute>
                  }
                /> */}
                <Route
                  path={USER_ROUTES.USER_COPY_TRADING_PROVIDER_DASHBOARD}
                  element={
                    <PrivateRoute>
                      <CopyTradingProviderDashboard />
                    </PrivateRoute>
                  }
                />
              </Route>
              {/* Admin Routes - for super_admin users */}
              <Route path="/admin" element={<AdminLayout />}>
                <Route
                  index
                  element={
                    <Navigate to={ADMIN_ROUTES.ADMIN_DASHBOARD} replace />
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_DASHBOARD}
                  element={
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_LEADS}
                  element={
                    <AdminRoute>
                      <Leads />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_PROFILE}
                  element={
                    <AdminRoute>
                      <Profile />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_CLIENTS}
                  element={
                    <AdminRoute>
                      <Clients />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_PAYMENTS}
                  element={
                    <AdminRoute>
                      <Payments />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_TRADES}
                  element={
                    <AdminRoute>
                      <Trades />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_REPORTS}
                  element={
                    <AdminRoute>
                      <Reports />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_SUPPORTS}
                  element={
                    <AdminRoute>
                      <AdminSupport />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_ACCOUNTS}
                  element={
                    <AdminRoute>
                      <AdminAccounts />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_IB_MARKETING}
                  element={
                    <AdminRoute>
                      <IBMarketing />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_SETTINGS}
                  element={
                    <AdminRoute>
                      <Settings />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_ECONOMIC_CALENDAR}
                  element={
                    <AdminRoute>
                      <AdminEconomicCalendar />
                    </AdminRoute>
                  }
                />
                {/* PAMM Admin Routes */}
                <Route
                  path={ADMIN_ROUTES.ADMIN_PAMM_DASHBOARD}
                  element={
                    <AdminRoute>
                      <PAMMDashboard />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_PAMM_STRATEGIES}
                  element={
                    <AdminRoute>
                      <PAMMStrategies />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_PAMM_INVESTORS}
                  element={
                    <AdminRoute>
                      <PAMMInvestors />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_PAMM_PERFORMANCE}
                  element={
                    <AdminRoute>
                      <PAMMPerformance />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_PAMM_ALLOCATIONS}
                  element={
                    <AdminRoute>
                      <PAMMAllocations />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_PAMM_COMMISSIONS}
                  element={
                    <AdminRoute>
                      <PAMMCommissions />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_PAMM_TRADES}
                  element={
                    <AdminRoute>
                      <PAMMTrades />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_PAMM_APPROVALS}
                  element={
                    <AdminRoute>
                      <PAMMApprovals />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_PAMM_REPORTS}
                  element={
                    <AdminRoute>
                      <PAMMReports />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_PAMM_ACCOUNTS}
                  element={
                    <AdminRoute>
                      <PAMMAccounts />
                    </AdminRoute>
                  }
                />
                {/* MAM Admin Routes */}
                <Route
                  path={ADMIN_ROUTES.ADMIN_MAM_DASHBOARD}
                  element={
                    <AdminRoute>
                      <MAMDashboard />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_MAM_ACCOUNTS}
                  element={
                    <AdminRoute>
                      <MAMAccounts />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_MAM_MANAGERS}
                  element={
                    <AdminRoute>
                      <MAMManagers />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_MAM_INVESTORS}
                  element={
                    <AdminRoute>
                      <MAMInvestors />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_MAM_PERFORMANCE}
                  element={
                    <AdminRoute>
                      <MAMPerformance />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_MAM_ALLOCATIONS}
                  element={
                    <AdminRoute>
                      <MAMAllocations />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_MAM_COMMISSIONS}
                  element={
                    <AdminRoute>
                      <MAMCommissions />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_MAM_TRADES}
                  element={
                    <AdminRoute>
                      <MAMTrades />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_MAM_REPORTS}
                  element={
                    <AdminRoute>
                      <MAMReports />
                    </AdminRoute>
                  }
                />
                {/* Copy Trading Admin Routes */}
                <Route
                  path={ADMIN_ROUTES.ADMIN_COPY_TRADING_DASHBOARD}
                  element={
                    <AdminRoute>
                      <CopyTradingDashboard />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_COPY_TRADING_STRATEGIES}
                  element={
                    <AdminRoute>
                      <CopyTradingStrategies />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_COPY_TRADING_MANAGERS}
                  element={
                    <AdminRoute>
                      <CopyTradingManagers />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_COPY_TRADING_FOLLOWERS}
                  element={
                    <AdminRoute>
                      <CopyTradingFollowers />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_COPY_TRADING_PERFORMANCE}
                  element={
                    <AdminRoute>
                      <CopyTradingPerformance />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_COPY_TRADING_TRADES}
                  element={
                    <AdminRoute>
                      <CopyTradingTrades />
                    </AdminRoute>
                  }
                />
                {/* <Route
                  path={ADMIN_ROUTES.ADMIN_COPY_TRADING_SETTINGS}
                  element={
                    <AdminRoute>
                      <CopyTradingSettings />
                    </AdminRoute>
                  }
                /> */}
                <Route
                  path={ADMIN_ROUTES.ADMIN_COPY_TRADING_REPORTS}
                  element={
                    <AdminRoute>
                      <CopyTradingReports />
                    </AdminRoute>
                  }
                />
                {/* Admin Trading Services Routes */}
                {/* <Route
                  path={ADMIN_ROUTES.ADMIN_TRADING_SERVICES_OVERVIEW}
                  element={
                    <AdminRoute>
                      <TradingServicesOverview />
                    </AdminRoute>
                  }
                /> */}
                <Route
                  path={ADMIN_ROUTES.ADMIN_STRATEGY_APPROVAL}
                  element={
                    <AdminRoute>
                      <AdminStrategyApproval />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_PAMM_APPROVAL}
                  element={
                    <AdminRoute>
                      <AdminPAMMApproval />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_RISK_MONITORING}
                  element={
                    <AdminRoute>
                      <AdminRiskMonitoring />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_AUDIT_LOGS}
                  element={
                    <AdminRoute>
                      <AdminAuditLogs />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_TRADING_PERFORMANCE}
                  element={
                    <AdminRoute>
                      <TradingPerformance />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_TRADING_TRADE_HISTORY}
                  element={
                    <AdminRoute>
                      <TradingTradeHistory />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_TRADING_FEES}
                  element={
                    <AdminRoute>
                      <TradingFees />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_TRADING_REPORTS}
                  element={
                    <AdminRoute>
                      <TradingReports />
                    </AdminRoute>
                  }
                />
              </Route>
              {/* 404 Page */}
              <Route path="*" element={<NotFound />} />
              {/* Unauthorized Page */}
              <Route path="/unauthorized" element={<Unauthorized />} />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
